/* @flow */
import { em } from 'polished';
import { baseFontSize } from '@braindate/ui/lib/token/typography';
import { lap } from '@braindate/ui/lib/token/mediaQuery';

import type { ThemeFunction } from 'src/shared/app/base/types/themeTypes';

const setAccentColor = ({
  theme,
  isDisabled,
  isActive,
}: ThemeFunction<{ isActive: boolean, isDisabled: boolean }>) =>
  !isDisabled || isActive ? theme.accentColor : theme.textColorSecondary;

const circleSize = em(40, baseFontSize);
const separatorDistance = em(40, baseFontSize);
const arrowSize = 10;
const arrowBorder = 1;

export default {
  root: {
    display: 'flex',
    position: 'relative',
    fontSize: '0.8em',
    justifyContent: 'center',
    flex: 1,
    color: setAccentColor,
    maxWidth: '20em',
    [lap]: {
      fontSize: '1em',
    },

    ['&:not(:first-child):after']: {
      content: '""',
      position: 'absolute',
      top: `calc(${circleSize} / 2)`,
      width: `calc(100% - ${circleSize} - ${separatorDistance})`,
      height: '1px',
      backgroundColor: ({
        theme,
        isDisabled,
        isActive,
        separatorColor,
      }: ThemeFunction<{
        isActive: boolean,
        isDisabled: boolean,
        separatorColor?: string,
      }>) =>
        separatorColor
          ? separatorColor({ theme })
          : setAccentColor({ theme, isDisabled, isActive }),
      left: `calc((100% - ${circleSize} - ${separatorDistance}) / 2 * -1)`,
    },
  },
  button: {
    background: 'none',
    borderRadius: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '&:disabled': {
      opacity: 1,
      color: 'inherit',
    },
    '&:hover $title': {
      textDecoration: ({ isDisabled }: ThemeFunction) =>
        !isDisabled ? 'underline' : 'none',
    },
  },
  circle: {
    width: circleSize,
    height: circleSize,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: ({ theme }: ThemeFunction) => theme.textColorReverse,
    backgroundColor: setAccentColor,
    marginBottom: ({ isActive }: ThemeFunction) =>
      isActive ? '0.45em' : '0.6em',
    textDecoration: 'none !important',
  },
  number: {
    fontWeight: 700,
    fontSize: ({ isActive }: ThemeFunction) =>
      em(isActive ? 20 : 16, baseFontSize),
    textDecoration: 'none !important',
  },
  icon: {},
  title: {
    fontSize: ({ isActive }: ThemeFunction<{ isActive: boolean }>) =>
      em(isActive ? 20 : 16, baseFontSize),
    fontWeight: ({ isActive }: ThemeFunction<{ isActive: boolean }>) =>
      isActive ? 700 : 400,
    marginBottom: '0.5rem',
    color: setAccentColor,
  },
  separatorArrow: {
    position: 'absolute',
    top: `calc(${circleSize} / 2)`,
    right: `calc((100% - ${circleSize} - ${separatorDistance}) / 2 * -1)`,
    transform: 'translateY(-50%)',
    height: arrowSize,
    width: arrowSize,
    display: 'inline-block',
    padding: 0,

    ['&:before, &:after']: {
      content: '""',
      position: 'absolute',
      height: arrowBorder,
      width: arrowSize,
      background: ({
        theme,
        separatorColor,
      }: ThemeFunction<{ separatorColor: string }>) =>
        separatorColor ? separatorColor({ theme }) : theme.textColorSecondary,
      borderRadius: '0.4rem',
      display: 'block',
    },
    ['&:before']: {
      transform: `translateX(calc(${arrowBorder}px * 2)) translateY(${arrowBorder}px) rotate(45deg)`,
    },
    ['&:after']: {
      transform: `translateX(calc(${arrowBorder}px * 2)) translateY(calc(${arrowSize}px - 2px)) rotate(-45deg)`,
    },
  },
};
